import { useMemo, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import {
  ActiveFilters,
  Button,
  COUNTRY_CODES,
  FacetFilter,
  describeConditions as describeFacetConditions,
  geoName,
  type Condition,
  type ConditionOperator,
  type Facet,
  type FacetConditionsValue,
  type FacetFilterDraft,
  type FacetFilterValue,
} from "@fox1ck-org/ui";

import { getLocale, plural, useT } from "@/lib/i18n";
import { useTrackerRoles } from "@/lib/permissions";
import { useListCampaignGroups } from "@/lib/services/campaign-service";
import { useAllOffers } from "@/lib/services/offer-service";
import { useAllTrafficSources } from "@/lib/services/traffic-source-service";

import { isPercentMetric, type ListColumn } from "./list-columns";
import { FACET_IDS, STATUS_OPTIONS, type ConditionOp, type FacetId, type Facets, type MetricCondition } from "./list-url";
import { useCampaignOwners } from "./use-campaign-list";

/**
 * The list's filters (board CampFilter): ONE «Фильтр» — the package's
 * FacetFilter over the dimensions (Баер, Источник, Группа, Гео, Оффер,
 * Статус) and «Метрики», the conditions on the period's numbers, in the same
 * popover and the same draft until «Применить». Its footer is the server's
 * count of what the draft would leave and «Сохранить как вид». «Баер» is a
 * chip even while it filters nothing («Баер Все»).
 */

/** The conditions facet's id: its value is `ListState.conditions`, not a dimension. */
export const METRICS_FACET = "metrics";
/** The chip that stays while it filters nothing (board CampList). */
const ALWAYS_SHOWN: readonly string[] = ["owner"];

const OP_LABEL: Record<ConditionOp, string> = { lt: "<", lte: "≤", gt: ">", gte: "≥", eq: "=", neq: "≠" };
const OPS = Object.keys(OP_LABEL) as ConditionOp[];

/** The list's conditions as the package's conditions record. Pure. */
export function toFacetConditions(cs: readonly MetricCondition[]): FacetConditionsValue {
  return cs.length > 0 ? { [METRICS_FACET]: cs.map((c): Condition => ({ field: c.metric, operator: c.op, value: c.value })) } : {};
}

/** The package's conditions record back as the list's conditions: rows with a number typed. Pure. */
export function fromFacetConditions(record: FacetConditionsValue): MetricCondition[] {
  return (record[METRICS_FACET] ?? []).flatMap((c) =>
    typeof c.value === "string" && c.value.trim() !== "" && (OPS as readonly string[]).includes(c.operator)
      ? [{ metric: c.field, op: c.operator as ConditionOp, value: c.value.trim() }]
      : [],
  );
}

/** The package's options record back as the list's dimension filters. Pure. */
export function fromFacetValue(value: FacetFilterValue): Facets {
  const out: Facets = {};
  for (const id of FACET_IDS) {
    const v = value[id];
    if (Array.isArray(v) && v.length > 0) out[id] = [...(v as readonly string[])];
  }
  return out;
}

/** The dimensions and «Метрики», in the board's order. `metrics` are the columns a condition can be on. */
export function useFacets(metrics: readonly ListColumn[]): Facet[] {
  const t = useT();
  const { isSelf } = useTrackerRoles();
  const owners = useCampaignOwners();
  const sources = useAllTrafficSources();
  const groups = useListCampaignGroups();
  const offers = useAllOffers({});
  return useMemo(() => {
    const locale = getLocale();
    const operators: ConditionOperator[] = OPS.map((op) => ({ value: op, label: OP_LABEL[op], ariaLabel: t(`camps.op.${op}`) }));
    const facets: Record<FacetId, Facet> = {
      owner: {
        id: "owner",
        label: t("camps.facet.owner"),
        kind: "options",
        loading: owners.isLoading,
        searchable: (owners.data?.length ?? 0) > 12,
        options: (owners.data ?? []).map((o) => ({
          value: o.id,
          label: o.id === "" ? t("camps.group.noOwner") : o.name || t("camps.group.buyer", { id: o.id.slice(0, 8) }),
          count: o.campaigns,
        })),
      },
      source: {
        id: "source",
        label: t("camps.facet.source"),
        kind: "options",
        loading: sources.isLoading,
        searchable: (sources.data?.items.length ?? 0) > 12,
        options: (sources.data?.items ?? []).map((s) => ({ value: s.id, label: s.name })),
      },
      group: {
        id: "group",
        label: t("camps.facet.group"),
        kind: "options",
        loading: groups.isLoading,
        searchable: (groups.data?.length ?? 0) > 12,
        options: [
          ...(groups.data ?? []).map((g) => ({ value: g.id, label: g.name, count: g.campaigns })),
          { value: "none", label: t("camps.group.noGroup") },
        ],
      },
      geo: {
        id: "geo",
        label: t("camps.facet.geo"),
        kind: "options",
        searchable: true,
        options: COUNTRY_CODES.map((c) => ({ value: c, label: `${c} · ${geoName(c, "country", locale)}` })),
      },
      offer: {
        id: "offer",
        label: t("camps.facet.offer"),
        kind: "options",
        loading: offers.isLoading,
        searchable: true,
        options: (offers.data?.items ?? []).map((o) => ({ value: o.id, label: o.name })),
      },
      status: {
        id: "status",
        label: t("camps.facet.status"),
        kind: "options",
        options: STATUS_OPTIONS.map((s) => ({ value: s, label: t(`camps.status.${s}`) })),
      },
    };
    const conditions: Facet = {
      id: METRICS_FACET,
      label: t("camps.facet.metrics"),
      kind: "conditions",
      heading: t("camps.metrics.title"),
      caption: t("camps.metrics.all"),
      columns: "minmax(0,1fr) 3.5rem 4.5rem",
      operators,
      fields: metrics.map((m) => ({ value: m.key, label: m.label, kind: "number", unit: isPercentMetric(m.def) ? "%" : undefined })),
      conditionLabels: {
        field: t("camps.metrics.field"),
        operator: t("camps.metrics.operator"),
        value: t("camps.metrics.value"),
        remove: t("camps.metrics.remove"),
      },
    };
    // «своё»: every campaign is his — no «Баер» facet.
    return [...(isSelf ? [] : [facets.owner]), facets.source, facets.group, facets.geo, facets.offer, conditions, facets.status];
  }, [t, isSelf, metrics, owners.data, owners.isLoading, sources.data, sources.isLoading, groups.data, groups.isLoading, offers.data, offers.isLoading]);
}

/** «ROI < 0 % · Расход > 150» — the conditions in the facet's own words. Pure. */
export function describeConditions(facets: readonly Facet[], cs: readonly MetricCondition[]): string {
  const facet = facets.find((f) => f.id === METRICS_FACET);
  return facet ? describeFacetConditions(facet, toFacetConditions(cs)[METRICS_FACET] ?? []) : "";
}

/** What the open filter would apply: the screen asks the server how many campaigns that leaves. */
export interface FilterDraft {
  facets: Facets;
  conditions: MetricCondition[];
}

export function CampaignFilter({
  facets,
  value,
  conditions,
  onApply,
  onDraft,
  found,
  onSaveAsView,
  compact,
}: {
  facets: Facet[];
  value: Facets;
  conditions: MetricCondition[];
  onApply: (facets: Facets, conditions: MetricCondition[]) => void;
  /** The draft changed, or the filter closed (undefined). */
  onDraft: (draft: FilterDraft | undefined) => void;
  /** The server's count for the draft; undefined while it is asked. */
  found: number | undefined;
  /** «Сохранить как вид»: the draft is applied, then the save dialog opens. */
  onSaveAsView: () => void;
  /**
   * The phone header's button (доска MCampaigns): the same filter behind an
   * icon with the number of filters on, without the chips row.
   */
  compact?: boolean;
}) {
  const t = useT();
  const [active, setActive] = useState<string | null>(null);
  const record = value as FacetFilterValue;
  const conditionRecord = useMemo(() => toFacetConditions(conditions), [conditions]);
  const apply = (next: FacetFilterValue, nextConditions: FacetConditionsValue) => onApply(fromFacetValue(next), fromFacetConditions(nextConditions));
  const draft = (d: FacetFilterDraft) => onDraft({ facets: fromFacetValue(d.value), conditions: fromFacetConditions(d.conditions) });
  const on = Object.values(value).filter((v) => v && v.length > 0).length + (conditions.length > 0 ? 1 : 0);
  return (
    <>
      {compact ? null : (
        <ActiveFilters
          value={record}
          conditions={conditionRecord}
          facets={facets}
          alwaysShow={ALWAYS_SHOWN}
          editingId={active}
          onRemove={(id) => (id === METRICS_FACET ? apply(record, {}) : apply({ ...record, [id]: [] }, conditionRecord))}
          onEdit={(id) => setActive(id)}
          labels={{
            list: t("camps.filter.active"),
            remove: (f) => t("camps.filter.remove", { name: f }),
            edit: (f) => t("camps.filter.edit", { name: f }),
            any: () => t("camps.filter.any"),
          }}
        />
      )}
      <FacetFilter
        trigger={
          compact ? (
            <Button variant="ghost" size="icon" className="relative size-11" aria-label={t("camps.filter.add")}>
              <SlidersHorizontal className="size-4.5" strokeWidth={1.5} />
              {on > 0 ? (
                <span className="absolute right-1.5 top-1.5 grid size-4 place-items-center rounded-full bg-primary font-mono text-xs text-primary-foreground">{on}</span>
              ) : null}
            </Button>
          ) : undefined
        }
        facets={facets}
        value={record}
        conditions={conditionRecord}
        onChange={apply}
        onDraftChange={draft}
        onOpenChange={(open) => {
          if (!open) onDraft(undefined);
        }}
        summary={
          <span className="text-xs text-muted-foreground">
            {found === undefined ? t("camps.metrics.counting") : t("camps.metrics.found", { count: found, campaigns: plural(t("word.campaigns"), found) })}
          </span>
        }
        extraAction={{ label: t("camps.metrics.saveAsView"), onSelect: onSaveAsView }}
        activeFacetId={active}
        onActiveFacetChange={setActive}
        labels={{
          trigger: t("camps.filter.add"),
          facets: t("camps.filter.facets"),
          search: (f) => t("camps.filter.search", { name: f }),
          apply: t("camps.filter.apply"),
          reset: t("camps.filter.reset"),
          noOptions: t("camps.filter.nothing"),
          loading: t("camps.filter.loading"),
          back: t("camps.filter.back"),
          addCondition: t("camps.metrics.addRow"),
          noConditions: t("camps.metrics.none"),
          conditionFormat: t("camps.metrics.format"),
        }}
      />
    </>
  );
}
